import React, { useEffect, useState, Fragment } from "react";
import { number, func, any, bool, string } from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { bookingsRequestData, clearErrors } from "../../redux/actions";
import { Button } from "../../common";
import * as style from "./styles.module.scss";
import LabeledValue from "../LabeledValue";
import AutocompleteBox from "../AutocompleteBox";
import { splitBookings } from "../../helpers/helpers";
import BookingsLoading from "../BookingsLoading";

const parseDate = (date, targetFormat) => {
  let day = date.getDate().toString();
  let month = (date.getMonth() + 1).toString();
  day = day.length > 1 ? day : `0${day}`;
  month = month.length > 1 ? month : `0${month}`;
  return targetFormat
    .replace("dd", day)
    .replace("mm", month)
    .replace("yyyy", date.getFullYear())
    .replace("yy", date.getFullYear().toString().substr(-2));
};

const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const addDaysToDate = (date, daysNumberToAdd) =>
  new Date(date.getTime() + daysNumberToAdd * 1000 * 60 * 60 * 24);
const getFirstDayOfWeek = (date) => addDaysToDate(date, -date.getDay());
const getCurrentWeek = () => {
  const currentDate = new Date();
  const firstDayOfWeek = getFirstDayOfWeek(currentDate);
  const currentWeek = {};
  days.forEach((day, index) => {
    currentWeek[day] = addDaysToDate(firstDayOfWeek, index);
  });
  return currentWeek;
};

const EmptyDataCell = () => <div className="calendar" />;

const EmptyRow = () => (
  <div>
    {Array.from(new Array(8)).map((e, _index) => (
      <EmptyDataCell key={`emptydatacell${_index}`} />
    ))}
  </div>
);

const EmptyCalendarBody = ({ rowsNumber }) => (
  <Fragment>
    {Array.from(new Array(rowsNumber)).map((e, _index) => (
      <EmptyRow key={`emptyrow${_index}`} />
    ))}
  </Fragment>
);

EmptyCalendarBody.propTypes = {
  rowsNumber: number,
};

const DesktopDataRow = ({ weekToDisplay, daysWorking }) =>
  days.map((day, index) => {
    const isWorking = daysWorking.includes(
      parseDate(weekToDisplay[day], "yyyy-mm-dd")
    );
    let isWorkingDayBefore;
    let isWorkingDayAfter;
    if (day !== "Sun") {
      isWorkingDayBefore = daysWorking.includes(
        parseDate(weekToDisplay[days[index - 1]], "yyyy-mm-dd")
      );
    }
    if (day !== "Sat") {
      isWorkingDayAfter = daysWorking.includes(
        parseDate(weekToDisplay[days[index + 1]], "yyyy-mm-dd")
      );
    }
    return isWorking ? (
      <div className={style.bookingDetails__row_day_wrapper} key={day}>
        <div
          className={`${style.bookingDetails__row_day} ${
            isWorking ? `${style.bookingDetails__filledRow}` : ""
          } ${
            isWorkingDayBefore ? "" : `${style.bookingDetails__leftRoundedRow}`
          } ${
            isWorkingDayAfter ? "" : `${style.bookingDetails__rightRoundedRow}`
          } `}
          key={day}
        >
          <span>{isWorking && day.toUpperCase()}</span>
        </div>
      </div>
    ) : (
      <div className={`${style.bookingDetails__row_day_empty}`} key={day} />
    );
  });

const CalendarHeader = ({ day, week }) => (
  <div
    className={`${style.bookingDetails__header_day} ${
      day.toLowerCase() !== "sat" && day.toLowerCase() !== "sun"
        ? "d-none d-md-flex"
        : ""
    } ${
      day.toLowerCase() === "sun" ? style.bookingDetails__header_day_sun : ""
    }`}
    key={day}
  >
    <span>{parseDate(week[day], "dd/mm/yy")}</span>
  </div>
);

CalendarHeader.propTypes = {
  day: any,
  week: any,
};

const CalendarDataRow = ({
  weekToDisplay,
  jobTitle,
  town,
  address,
  showTown = false,
  filtData,
  daysWorking,
  className = "",
}) => {
  const dataRowProps = { weekToDisplay, daysWorking };
  return filtData.includes(true) ? (
    <section className={`${style.bookingDetails__row} ${className}`}>
      <div className={style.bookingDetails__row_vacancy}>
        <p className={style.bookingDetails__row_vacancy_inner}>{jobTitle}</p>
        {showTown ? (
          <p className={style.bookingDetails__row_vacancy_town}>
            {address}, {town}
          </p>
        ) : (
          ""
        )}
      </div>
      <DesktopDataRow {...dataRowProps} />
    </section>
  ) : null;
};

CalendarDataRow.propTypes = {
  booking: any,
  weekToDisplay: any,
  jobTitle: string,
  town: string,
  address: any,
  showTown: bool,
  filtData: any,
  daysWorking: any,
  className: string,
};

const CalendarDays = ({ weekToDisplay }) => (
  <header className={`${style.bookingDetails__header}`}>
    <div className={style.bookingDetails__header_vacancy}>
      <span>VACANCY</span>
    </div>
    <div
      className={`d-flex justify-content-center ${style.bookingDetails__header_days}`}
    >
      {days.map((day) => (
        <CalendarHeader key={day} day={day} week={weekToDisplay} />
      ))}
    </div>
  </header>
);

CalendarDays.propTypes = {
  weekToDisplay: any,
};

const CalendarTable = ({
  booking,
  weekToDisplay,
  showHeader = true,
  className = "",
  isAll = false,
}) => {
  return (
    <Fragment>
      {showHeader && <CalendarDays weekToDisplay={weekToDisplay} />}
      {booking.bookings.map((innerBooking) => {
        const daysWorking = innerBooking.bookedDates.map(
          (day) => day.split("T")[0]
        );
        const filtData = days.map((day) =>
          innerBooking.bookedDates
            .map((innerDay) => innerDay.split("T")[0])
            .includes(parseDate(weekToDisplay[day], "yyyy-mm-dd"))
        );
        return filtData.includes(true) ? (
          <section
            key={innerBooking.eventRef}
            className={`${style.bookingDetails__rows}`}
          >
            <CalendarDataRow
              booking={innerBooking}
              weekToDisplay={weekToDisplay}
              jobTitle={booking.jobTitle}
              town={booking.location.postTown}
              address={booking.location.line1}
              showTown={isAll}
              filtData={filtData}
              daysWorking={daysWorking}
              className={className}
            />
          </section>
        ) : (
          ""
        );
      })}
      {booking.length === 0 && <EmptyCalendarBody rowsNumber={4} />}
    </Fragment>
  );
};

CalendarTable.propTypes = {
  booking: any,
  weekToDisplay: any,
  isAll: bool,
  showHeader: bool,
  className: string,
};

const BookingDetails = ({
  requestBookingsData,
  bookings = undefined,
  isLoading,
  bookingRef,
  isPast,
  isAll,
}) => {
  const bookingShown =
    (bookings &&
      bookings.find((_booking) => _booking.vacancyRef === bookingRef)) ||
    {};

  const [weekOffset, setWeekOffset] = useState(0);
  const calculateInitialWeekOffset = (bookedDates) => {
    if (isPast) {
      const latestBooking = new Date(bookedDates.sort().reverse()[0]);
      const latestBookingDay = Math.ceil(latestBooking / 1000 / 3600 / 24);
      const currentDayOfWeek = new Date().getDay();
      const today = Math.floor(new Date() / 1000 / 3600 / 24);
      const diffWeeks = Math.floor(
        (latestBookingDay - today + currentDayOfWeek) / 7
      );
      setWeekOffset(diffWeeks);
    }
  };

  useEffect(() => {
    if (!isLoading && bookings === undefined) {
      requestBookingsData();
    }
    (() =>
      bookingShown.bookings &&
      calculateInitialWeekOffset(bookingShown.bookings[0].bookedDates))();
  }, [bookingShown]);

  const switchToPreviousWeek = () => setWeekOffset(weekOffset - 1);
  const switchToNextWeek = () => setWeekOffset(weekOffset + 1);
  const currentWeek = getCurrentWeek();

  const getWeekToDisplay = (_currentWeek, offset) => {
    const newWeek = {};
    days.forEach((day) => {
      newWeek[day] = addDaysToDate(_currentWeek[day], 7 * offset);
    });
    return newWeek;
  };
  const dividedBookings = bookings && splitBookings(bookings);
  const allBookings =
    dividedBookings &&
    dividedBookings.currentBookings.concat(dividedBookings.pastBookings);

  return bookings ? (
    <div className={`${style.bookingDetails__container}`}>
      <h2>
        {isAll ? "All vacancies" : bookingShown.jobTitle} - Booking details
      </h2>
      <Link to="/profile/edit/Bookings">
        <Button
          classes="btn btn-md px-2 
          btn-outline-info my-2"
        >
          <b className="text-black">Back to overview</b>
        </Button>
      </Link>
      <div className={`${style.bookingDetails__wrapper}`}>
        <section>
          <section className="d-flex justify-content-between flex-wrap">
            <div className={style.bookingDetails__titlesection}>
              <section className="mt-1 mt-lg-0">
                {bookingShown.location && (
                  <LabeledValue
                    className="px-1 px-sm-0"
                    header="address"
                  >{`${bookingShown.location.line1}\n${bookingShown.location.postTown}, ${bookingShown.location.zipCode}`}</LabeledValue>
                )}
              </section>
              <AutocompleteBox
                disabled={isLoading}
                items={
                  allBookings &&
                  allBookings.map((_booking) => {
                    return {
                      title: _booking.jobTitle,
                      subtitle: _booking.location.postTown,
                      link: `/profile/edit/Bookings/${_booking.vacancyRef}/${
                        _booking.isPast ? "past" : ""
                      }`,
                    };
                  })
                }
              />
            </div>
            <nav className={` ${style.bookingDetails__buttonContainer}`}>
              <div className={`d-flex w-100`}>
                <button
                  className={`btn btn-block ${style.bookingDetails__weekButton}`}
                  onClick={switchToPreviousWeek}
                >
                  <b>Previous week</b>
                </button>
                <button
                  className={`btn btn-block m-0 ml-1 ${style.bookingDetails__weekButton}`}
                  onClick={switchToNextWeek}
                >
                  <b>Next week</b>
                </button>
              </div>
            </nav>
          </section>
          <span className={` ${style.bookingDetails__rows}`}>
            {isAll
              ? bookings
                  .sort((book1, book2) => {
                    if (book1.jobTitle > book2.jobTitle) return 1;
                    if (book1.jobTitle < book2.jobTitle) return -1;
                    if (book1.location.postTown > book2.location.postTown)
                      return 1;
                    if (book1.location.postTown < book2.location.postTown)
                      return -1;
                    return 0;
                  })
                  .map((_booking, i) => (
                    <CalendarTable
                      key={i}
                      booking={_booking}
                      weekToDisplay={getWeekToDisplay(currentWeek, weekOffset)}
                      showHeader={i === 0}
                      isAll
                    />
                  ))
              : bookingShown && (
                  <CalendarTable
                    booking={bookingShown}
                    weekToDisplay={getWeekToDisplay(currentWeek, weekOffset)}
                    className={`${style.bookingDetails__single}`}
                  />
                )}
          </span>
        </section>
      </div>
    </div>
  ) : (
    <BookingsLoading />
  );
};

BookingDetails.propTypes = {
  bookingRef: number,
  requestBookingsData: func,
  bookings: any,
  isLoading: bool,
  isPast: bool,
  isAll: bool,
};

const mapStateToProps = ({
  core: {
    bookings: { isLoading, bookings },
    errors,
  },
}) => ({
  isLoading,
  bookings,
  errors,
});

const mapDispatchToProps = {
  requestBookingsData: bookingsRequestData,
  clearErrorsWithSource: clearErrors,
};

const withRedux = connect(mapStateToProps, mapDispatchToProps);

export default withRedux(BookingDetails);
